import { useMemo } from 'react'
import css from './views.module.css'
import { Card, Disclosure, QueryError } from '../ui'
import { useLinearTickets, useLinearTicketFeed } from '../data/queries'
import { toLinearTicketList, type LinearTicket } from '../data/map/linearTickets'
import { linearTicketsLine } from '../data/map/summaries'
import { age } from '../lib/time'

/**
 * The department's own open Linear tickets: the work on its desk that no check
 * found and no run is doing -- fixes a person makes, decisions, follow-ups.
 *
 * Renders nothing for a department whose tickets are not published (only those in
 * the watchdog's desk_tickets config are), so it sits on every desk without naming
 * one (product rule 2). A ticket attached to an open finding is on the finding
 * above, not repeated here.
 *
 * A copy, and it says so: "as of" is the watchdog's last publish, and a list older
 * than the watchdog's own liveness window is marked, the same threshold the cost
 * strip uses. Nothing here is a control; each row opens the ticket in Linear.
 */
export function DeskLinearTickets({ slug }: { slug: string }) {
  const tickets = useLinearTickets(slug)
  const feed = useLinearTicketFeed(slug)
  const list = useMemo(() => toLinearTicketList(tickets.data ?? [], feed.data ?? []), [tickets.data, feed.data])

  if (tickets.error) return <QueryError error={tickets.error} />
  if (feed.error) return <QueryError error={feed.error} />
  if (!tickets.data || !feed.data || !list.published) return null

  return (
    <Card style={{ marginBottom: 14 }}>
      {/* Closed to its count unless it is short enough to be the count. The
          staleness line stays outside, because it qualifies the count too. */}
      <Disclosure
        label
        title="Tickets"
        defaultOpen={list.tickets.length <= 1}
        summary={
          `${linearTicketsLine(list.tickets)} · in Linear, most urgent first · as of ${age(list.asOf)}`
          + (list.onFindings > 0
            ? ` · ${list.onFindings} more ${list.onFindings === 1 ? 'is' : 'are'} on the findings above`
            : '')
        }
        aside={list.stale ? (
          <div className={css.ticketFail} style={{ marginTop: 0 }}>
            Not refreshed in over half an hour, so this list may be out of date. Linear has the current one.
          </div>
        ) : null}
      >
        {list.tickets.length === 0 ? (
          <p className={css.findingNone}>Nothing open that is not already on a finding.</p>
        ) : (
          list.tickets.map((t) => <TicketRow key={t.ref} ticket={t} />)
        )}
      </Disclosure>
    </Card>
  )
}

function priorityTone(p: number): string {
  if (p === 1 || p === 2) return 'var(--s-fail)'
  if (p === 3) return 'var(--s-wait)'
  return 'var(--ink-3)'
}

function TicketRow({ ticket: t }: { ticket: LinearTicket }) {
  return (
    <div className={css.findingRow}>
      <div className="mono" style={{ fontSize: 12 }}>
        <span style={{ color: priorityTone(t.priority) }}>{t.priorityLabel.toLowerCase()}</span>
        <div className={css.ticketAge}>{t.state.toLowerCase()}</div>
      </div>
      <div>
        <div className={css.ticketTitle}>
          <a href={t.url} target="_blank" rel="noreferrer">{t.ref}</a> {t.title}
        </div>
        <div className={`${css.ticketLanes} mono`}>
          {t.assignee ? <span>{t.assignee}</span> : <span>unassigned</span>}
          {t.labels.map((l) => <span key={l}>{l}</span>)}
          {t.updatedAt ? <span>updated {age(t.updatedAt)}</span> : null}
        </div>
      </div>
    </div>
  )
}
